import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { formatGender, isEmpty } from "../utils/global";
import { ICONS, LOGOS } from "../utils/assets";
import { PopUp } from "../components/PopUp";
import Button from "../components/form/Button";

export const EmailReturn = () => {
  const [details, setDetails] = useState({});
  const [completedOrder, setCompletedOrder] = useState("");
  const [motive, setMotive] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  const [showPopUp, setShowPopUp] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const orderLineID = getOrderLineID();

    if (!orderLineID) {
      window.location.href = "https://www.quickmove.co.nz/";
      return;
    }

    getOrderLineDetails();
  }, []);

  const hasName = useMemo(() => {
    return (
      details.To_Given_Name || details.To_Middle_Name || details.To_Family_Name
    );
  }, [details]);

  const IDVerification = useMemo(() => {
    return details.Verified ? "Verified" : "Not Verified";
  }, [details]);

  const fullAddress = useMemo(() => {
    const addressObj = {
      streetName: details.To_Address_Street_Name,
      buildingName: details.To_Address_Building_Name,
      unitNumber: details.To_Address_Unit_Number,
      suburb: details.To_Address_Suburb,
      city: details.To_Address_City,
      stateRegion: details.To_Address_State_Region,
      postCode: details.To_Address_Post_Code,
      country: details.To_Address_Country,
    };

    const addressArray = Object.values(addressObj);

    if (addressArray.every((value) => !value) || !addressArray) return "";

    return addressArray
      .filter((value) => value && value.trim() !== "")
      .join(", ");
  }, [details]);

  async function getOrderLineDetails() {
    setLoadingDetails(true);
    const orderLineID = getOrderLineID();
    // const accessToken = await getAccessToken();

    // if (!accessToken) {
    //   setLoadingDetails(false);
    //   setPopUpTitle("Error");
    //   setPopUpMessage("This URL is no longer valid.");
    //   setShowPopUp(true);
    //   return;
    // }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/orders/get-order-details`,
        { Order_Line_ID: orderLineID }
        // { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setDetails(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDetails(false);
    }
  }

  async function updateOrderLineStatus() {
    const orderLineID = getOrderLineID();
    setLoadingUpdate(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_URL}/orders/update-order-line-status`,
        {
          Order_Line_ID: orderLineID,
          Line_Status: completedOrder === "yes" ? 3 : 2,
          Status_Message: motive || null,
        }
      );

      setShowPopUp(true);
      setPopUpTitle("Success");
      setPopUpMessage("The order was updated successfully.");
    } catch (error) {
      setShowPopUp(true);
      setPopUpTitle("Error");
      setPopUpMessage(
        "An error occurred while updating the order. Please try again later."
      );
      console.error(error);
    } finally {
      setLoadingUpdate(false);
    }
  }

  const getOrderID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("order");
  };

  const getOrderLineID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("order-line");
  };

  const getRefreshToken = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("refresh_token");
  };

  const getAccessToken = async () => {
    try {
      const response = await axios.post("/authentication/get-token", {
        refresh_token: getRefreshToken(),
      });
      return response.data.access_token;
    } catch (error) {
      console.error("Error getting access token:", error);
      return null;
    }
  };

  const handleCompletedOrder = (value) => {
    setCompletedOrder(value);
    clearInputError("completedOrder");
    clearInputError("motive");

    if (value === "yes") {
      setMotive("");
    }
  };

  const handleMotiveInput = (value) => {
    setMotive(value);
    clearInputError("motive");
  };

  const updateOrder = async () => {
    const isValid = await validateFields();

    if (!isValid) return;

    await updateOrderLineStatus();
  };

  const clearInputError = (name) => {
    const errorKey = name;

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      delete newErrors[errorKey];
      return newErrors;
    });
  };

  const validateFields = async () => {
    let isValid = true;
    let newErrors = {};

    if (!completedOrder) {
      newErrors["completedOrder"] = "Please select an option.";
      isValid = false;
    }
    if (completedOrder === "no" && isEmpty(motive)) {
      newErrors["motive"] =
        "Please enter the reason for not completing the order.";
      isValid = false;
    }

    setErrors(newErrors);

    return isValid;
  };

  return (
    <>
      <PopUp
        open={showPopUp}
        title={popUpTitle}
        text={popUpMessage}
        onClose={() => setShowPopUp(false)}
      />

      <section className="step__container is-email-return">
        <div className="step__content">
          <div className="email-return">
            <div className="step__header text-center">
              <img
                className="logo"
                src={LOGOS.QUICKMOVE_FLOW}
                alt="Quickmove logo"
              />
              <div>
                <h1 className="text-md text-md--variant">Hello!</h1>
                <p className="paragraph-md">
                  You have an order to be completed..
                </p>
                <p className="paragraph-md">
                  Order Number:{" "}
                  <span className="text-neutral-950">{getOrderID()}</span>
                </p>
              </div>
            </div>

            <div className="email-return__details">
              <h2 className="paragraph-md text-neutral-950 semibold">
                Details to be updated
              </h2>

              {loadingDetails && <p className="paragraph-md">Loading...</p>}

              {!details.Order_ID && !loadingDetails && (
                <p className="paragraph-md">No information found.</p>
              )}

              {details.Account_ID && (
                <div className="email-return__row">
                  <div className="email-return__column">
                    <img
                      src={ICONS.ID_BADGE_ICON}
                      alt="Identification badge icon"
                    />
                    <div>
                      <p className="paragraph-md semibold text-neutral-950">
                        Customer Account
                      </p>
                      <p className="paragraph-md">{details.Account_ID}</p>
                    </div>
                  </div>
                </div>
              )}
              {hasName && (
                <div className="email-return__row">
                  {details.To_Given_Name && (
                    <div className="email-return__column">
                      <img src={ICONS.PROFILE_ICON} alt="Profile icon" />
                      <div>
                        <p className="paragraph-md semibold text-neutral-950">
                          Given Name
                        </p>
                        <p className="paragraph-md">{details.To_Given_Name}</p>
                      </div>
                    </div>
                  )}
                  {details.To_Middle_Name && (
                    <div className="email-return__column">
                      <img src={ICONS.PROFILE_ICON} alt="Profile icon" />
                      <div>
                        <p className="paragraph-md semibold text-neutral-950">
                          Middle Name
                        </p>
                        <p className="paragraph-md">{details.To_Middle_Name}</p>
                      </div>
                    </div>
                  )}
                  {details.To_FamilyName && (
                    <div className="email-return__column">
                      <img src={ICONS.PROFILE_ICON} alt="Profile icon" />
                      <div>
                        <p className="paragraph-md semibold text-neutral-950">
                          Family Name
                        </p>
                        <p className="paragraph-md">{details.To_FamilyName}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {details.To_Gender !== details.From_Gender && (
                <div className="email-return__row">
                  <div className="email-return__column">
                    <img src={ICONS.GENDER_ICON} alt="Gender icon" />
                    <div>
                      <p className="paragraph-md semibold text-neutral-950">
                        Gender
                      </p>
                      <p className="paragraph-md">
                        {formatGender(details.To_Gender)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {fullAddress && (
                <div className="email-return__row">
                  <div className="email-return__column">
                    <img src={ICONS.ADDRESS_ICON} alt="Address icon" />
                    <div>
                      <p className="paragraph-md semibold text-neutral-950">
                        Address
                      </p>
                      <p className="paragraph-md">{fullAddress}</p>
                    </div>
                  </div>
                </div>
              )}
              {details.To_Email && (
                <div className="email-return__row">
                  <div className="email-return__column">
                    <img src={ICONS.EMAIL_ICON} alt="E-mail icon" />
                    <div>
                      <p className="paragraph-md semibold text-neutral-950">
                        E-mail
                      </p>
                      <p className="paragraph-md">{details.To_Email}</p>
                    </div>
                  </div>
                </div>
              )}
              {details.To_Mobile_Number && (
                <div className="email-return__row">
                  <div className="email-return__column">
                    <img src={ICONS.PHONE_ICON} alt="Phone icon" />
                    <div>
                      <p className="paragraph-md semibold text-neutral-950">
                        Phone
                      </p>
                      <p className="paragraph-md">{details.To_Mobile_Number}</p>
                    </div>
                  </div>
                </div>
              )}
              {/* <div className="email-return__row">
                <div className="email-return__column">
                  <img src={ICONS.UPLOAD_FILE_ICON} alt="Upload file icon" />
                  <div>
                    <p className="paragraph-md semibold text-neutral-950">
                      Document attached
                    </p>
                    <p className="paragraph-md">{"TO DO"}</p>
                  </div>
                </div>
              </div> */}
              {details.Verified && (
                <div className="email-return__row">
                  <div className="email-return__column">
                    <img
                      src={ICONS.ID_CARD_ICON}
                      alt="Identification card icon"
                    />
                    <div>
                      <p className="paragraph-md semibold text-neutral-950">
                        ID Verification
                      </p>
                      <p className="paragraph-md">{IDVerification || "N/A"}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {details.Line_Status && details.Line_Status !== 3 && (
              <>
                <div className="email-return__completed">
                  <h3 className="text-md text-md--variant">
                    Have you completed the order?
                  </h3>

                  <div className="radio-group">
                    <div className="radio-group__item">
                      <input
                        type="radio"
                        name="completed"
                        id="yes"
                        value="yes"
                        onChange={(e) => handleCompletedOrder(e.target.value)}
                      />
                      <label htmlFor="yes">Yes</label>
                    </div>
                    <div className="radio-group__item">
                      <input
                        type="radio"
                        name="completed"
                        id="no"
                        value="no"
                        onChange={(e) => handleCompletedOrder(e.target.value)}
                      />
                      <label htmlFor="no">No</label>
                    </div>
                  </div>

                  {completedOrder === "no" && (
                    <div className="form__controller">
                      <label htmlFor="reason">
                        Reason for not completing the order
                      </label>
                      <textarea
                        name="reason"
                        id="reason"
                        rows="1"
                        maxLength={200}
                        placeholder="Write here..."
                        style={{ resize: "none" }}
                        value={motive}
                        onInput={(e) => handleMotiveInput(e.target.value)}
                      ></textarea>
                    </div>
                  )}
                  {errors.motive && (
                    <p className="paragraph-md text-danger">{errors.motive}</p>
                  )}
                  {errors.completedOrder && (
                    <p className="paragraph-md text-danger">
                      {errors.completedOrder}
                    </p>
                  )}
                </div>
                <div className="email-return__buttons">
                  <Button
                    variant="primary"
                    loading={loadingUpdate}
                    onClick={() => updateOrder()}
                  >
                    Update Order
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
